<template>
  <div class="FoldersView" @click.self="close">
    <div
      ref="dialog"
      class="FoldersView-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="folders-view-title"
      tabindex="-1"
      @click.stop
      @keydown.esc="close"
    >
      <header class="FoldersView-header">
        <h2 id="folders-view-title">Выбор папки</h2>
        <button
          type="button"
          class="FoldersView-close"
          aria-label="Закрыть"
          @click="close"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>
      </header>

      <nav class="FoldersView-crumbs" aria-label="Путь к папке">
        <button
          type="button"
          class="crumb"
          :class="{ current: !path }"
          @click="goTo('')"
        >
          Галерея
        </button>
        <template v-for="(crumb, index) in crumbs" :key="crumb.path">
          <span class="sep" aria-hidden="true">/</span>
          <button
            type="button"
            class="crumb"
            :class="{ current: index === crumbs.length - 1 }"
            :aria-current="index === crumbs.length - 1 ? 'location' : null"
            @click="goTo(crumb.path)"
          >
            {{ crumb.label }}
          </button>
        </template>
      </nav>

      <div class="FoldersView-body">
        <p v-if="loading" class="state">Загрузка папок…</p>

        <div v-else-if="error" class="state state--error">
          <span>{{ error }}</span>
          <button type="button" class="link" @click="reload">Повторить</button>
        </div>

        <p v-else-if="!folderList.length" class="state">
          В этой папке пока нет вложенных папок
        </p>

        <div v-else class="folders">
          <div
            v-for="folder in folderList"
            :key="folder"
            class="folder"
            :class="{ active: folder === selected }"
          >
            <button
              type="button"
              class="folder-main"
              :aria-pressed="folder === selected"
              :title="folder"
              @click="selectFolder(folder)"
              @dblclick="openFolder(folder)"
              @keydown.enter.prevent="openFolder(folder)"
            >
              <svg class="folder-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
                />
              </svg>
              <span class="folder-name">{{ folder }}</span>
            </button>
            <button
              type="button"
              class="folder-open"
              :aria-label="'Открыть папку ' + folder"
              @click="openFolder(folder)"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <polyline points="9 6 15 12 9 18" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <footer class="FoldersView-footer">
        <form class="create" @submit.prevent="createFolder">
          <input
            v-model="folderName"
            type="text"
            placeholder="Имя новой папки"
            aria-label="Имя новой папки"
            :disabled="creating"
          />
          <button
            type="submit"
            class="secondary"
            :disabled="!canCreate || creating"
          >
            {{ creating ? "Создаём…" : "Создать" }}
          </button>
        </form>

        <p class="create-error" v-if="createError">{{ createError }}</p>

        <div class="nav-actions">
          <span class="target" :title="targetPath">
            {{ selected ? "Выбрано: " + targetPath : "Текущая папка: " + pathLabel }}
          </span>
          <button
            type="button"
            class="secondary"
            :disabled="!path"
            @click="back"
          >
            Назад
          </button>
          <button
            type="button"
            class="primary"
            :disabled="loading || creating"
            @click="pickFolder"
          >
            Выбрать папку
          </button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import Api from "@/api/images.js";

const joinPath = (base, name) => (base ? base + "/" + name : name);
const parentPath = (path) => path.split("/").slice(0, -1).join("/");

export default {
  data() {
    return {
      folders: null,
      path: "",
      selected: null,
      folderName: "",
      loading: false,
      error: "",
      creating: false,
      createError: "",
      previouslyFocused: null,
      previousOverflow: "",
      requestToken: 0,
      requestedPath: "",
    };
  },
  computed: {
    folderList() {
      const list = this.folders && this.folders.folders;
      return Array.isArray(list) ? list : [];
    },
    crumbs() {
      if (!this.path) return [];
      const parts = this.path.split("/");
      return parts.map((part, index) => ({
        label: part,
        path: parts.slice(0, index + 1).join("/"),
      }));
    },
    canCreate() {
      return this.folderName.trim().length > 0;
    },
    targetPath() {
      return this.selected ? joinPath(this.path, this.selected) : this.path;
    },
    pathLabel() {
      return this.path || "корень галереи";
    },
  },
  methods: {
    close() {
      this.$emit("close");
    },
    async reload() {
      await this.getFolder(this.requestedPath);
    },
    async getFolder(path) {
      // Токен защищает от гонки: ответ на устаревший запрос игнорируется
      const token = ++this.requestToken;
      // Запоминаем, какой путь запрошен — по нему работает «Повторить»
      // и от него считается выбор, пока ответ не пришёл
      this.requestedPath = path;
      // Если фокус был в сетке, после перерисовки вернём его обратно
      const focusWasInGrid =
        document.activeElement &&
        !!document.activeElement.closest(".FoldersView-body");

      this.loading = true;
      this.error = "";
      try {
        const response = await Api.getFolders({ folder: path });
        if (token !== this.requestToken) return;
        this.folders = response.data;
        this.path = path;
        this.selected = null;
      } catch (e) {
        if (token !== this.requestToken) return;
        this.error = "Не удалось загрузить папки: " + (path || "корень галереи");
        this.folders = null;
      } finally {
        if (token === this.requestToken) {
          this.loading = false;
          if (focusWasInGrid) {
            this.$nextTick(() => {
              const first = this.$el.querySelector(".folder-main");
              const dialog = this.$refs.dialog;
              if (first) first.focus();
              else if (dialog) dialog.focus();
            });
          }
        }
      }
    },
    goTo(path) {
      // Повторный клик по уже открытой папке ничего не делает,
      // но во время загрузки он должен отменять чужой переход
      if (path === this.requestedPath && !this.error && !this.loading) return;
      this.getFolder(path);
    },
    back() {
      if (!this.path) return;
      this.goTo(parentPath(this.path));
    },
    selectFolder(folder) {
      this.selected = folder === this.selected ? null : folder;
    },
    openFolder(folder) {
      this.goTo(joinPath(this.path, folder));
    },
    pickFolder() {
      if (this.loading || this.creating) return;
      this.$emit("pickFolder", this.targetPath);
    },
    async createFolder() {
      const name = this.folderName.trim();
      if (!name || this.creating) return;

      if (this.folderList.includes(name)) {
        this.createError = "Папка с таким именем уже есть";
        return;
      }

      // Папка создаётся там, где пользователь был в момент отправки
      const target = this.path;
      // Пока идёт запрос, поле и кнопка отключены — фокус придётся вернуть
      const focusWasInCreate =
        document.activeElement &&
        !!document.activeElement.closest(".create");

      this.creating = true;
      this.createError = "";
      try {
        await Api.createFolder({ folderName: name, folder: target });
        this.folderName = "";

        // Пока шёл запрос, могли уйти в другую папку — тогда не дёргаем вид
        if (this.requestedPath !== target) return;

        await this.getFolder(target);
        // Выделяем созданную папку, только если она реально пришла в списке
        this.selected = this.folderList.includes(name) ? name : null;
      } catch (e) {
        this.createError = "Не удалось создать папку";
      } finally {
        this.creating = false;
        if (focusWasInCreate) {
          this.$nextTick(() => {
            const input = this.$el.querySelector(".create input");
            if (input) input.focus();
          });
        }
      }
    },
    onKeydown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== "Tab" || !this.$refs.dialog) return;

      // Держим Tab внутри диалога, пока он открыт
      const focusable = this.$refs.dialog.querySelectorAll(
        'button:not(:disabled), input:not(:disabled), select:not(:disabled), ' +
          'textarea:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === this.$refs.dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
  },
  mounted() {
    // Запоминаем, откуда открыли окно, чтобы вернуть туда фокус
    this.previouslyFocused = document.activeElement;
    this.previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    this.getFolder("");
    document.addEventListener("keydown", this.onKeydown);
    this.$nextTick(() => {
      if (this.$refs.dialog) this.$refs.dialog.focus();
    });
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.onKeydown);
    document.body.style.overflow = this.previousOverflow || "";

    const target = this.previouslyFocused;
    if (
      target &&
      target !== document.body &&
      target !== document.documentElement &&
      document.contains(target) &&
      typeof target.focus === "function"
    ) {
      target.focus();
    }
  },
};
</script>
